/**
 * Annotation controller for one sheet: owns the engine hook, the overlay store, tool preferences,
 * selection and the panels, composes the text box (useTextBox) and connector flow
 * (useConnectorFlow), and wires edit-mode input (useEditInput) to engine changes. All data goes
 * through the engine contract (src/client/annotations/contract.ts).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  HIGHLIGHT_WIDTHS,
  PEN_WIDTHS,
  Q,
  quant,
  unquant,
  type AnnotationKind,
  type AnnotationObject,
  type AnnotationOf,
  type DataByKind,
} from '../../shared/annotations';
import type { SongFileDTO } from '../../shared/dto/library';
import type { SheetAnnotations } from '../annotations/contract';
import { useSheetAnnotations } from '../annotations/useSheetAnnotations';
import { useToast } from '../components/toast';
import { useT } from '../i18n';
import { FINGER_DRAW_EVENT, readFingerDraw, type FingerDrawPref } from '../screens/settings/fingerDraw';
import type { ConnectorPin, PagePoint, ViewerHandle } from '../viewer/types';
import { isEditableTarget, pageClientRect } from './dom';
import {
  clampDelta,
  cssToUnits,
  effectiveScale,
  hitContext,
  HIT_TOLERANCE_PX,
  isSized,
  normToUnits,
  pageHeightUnits,
  scaleData,
  sizeOf,
  stepSize,
  topmostHit,
  translateData,
  type Pt,
} from './geometry';
import { fingerGrabsSelection, fingerRole, fingersDraw } from './gesture';
import { quantiseStroke, type RawPt } from './ink';
import {
  LONG_PRESS_TOLERANCE_PX,
  longPressSelects,
  onSelectionArea,
  resolveTap,
  selectionScalable,
  tapSelects,
  tapTolerancePx,
  type TapAction,
} from './routing';
import { OverlayStore } from './store';
import {
  loadRecentGlyphs,
  loadToolPrefs,
  pushRecent,
  readCoachDone,
  readPenSeen,
  saveRecentGlyphs,
  saveToolPrefs,
  writeCoachDone,
  writePenSeen,
  type ToolId,
  type ToolPrefs,
} from './tools';
import { useConnectorFlow } from './useConnectorFlow';
import { useEditInput, type EditInputLive, type TapFrom } from './useEditInput';
import { useTextBox, type AddObject } from './useTextBox';

/** Offset (quantised) of a duplicated object. */
const DUPLICATE_OFFSET = 150;

function toPins(list: readonly AnnotationOf<'connector'>[]): ConnectorPin[] {
  return list.map((c) => ({
    id: c.id,
    page: c.page,
    x: unquant(c.data.at[0]),
    y: unquant(c.data.at[1]),
    target: { page: c.data.target.page, x: unquant(c.data.target.at[0]), y: unquant(c.data.target.at[1]) },
    label: c.data.label ? c.data.label : undefined,
    color: c.data.color,
    size: c.data.size,
  }));
}

/** Engine connectors → viewer pins, keeping the same array while nothing changed (fewer page re-renders). */
function useStablePins(list: readonly AnnotationOf<'connector'>[]): ConnectorPin[] {
  const [cache] = useState(() => ({ key: '', pins: [] as ConnectorPin[] }));
  return useMemo(() => {
    const key = list.map((c) => `${c.id}@${c.hlcMs}`).join('|');
    if (key !== cache.key) {
      cache.key = key;
      cache.pins = toPins(list);
    }
    return cache.pins;
  }, [list, cache]);
}

export function useAnnotate(file: SongFileDTO, editing: boolean) {
  const t = useT();
  const toast = useToast();
  const engine = useSheetAnnotations(file);
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const [store] = useState(() => new OverlayStore());
  const pages = useMemo(() => file.pages ?? [], [file.pages]);

  /* ── viewer handle ── */
  const [handle, setHandleState] = useState<ViewerHandle | null>(null);
  const handleRef = useRef<ViewerHandle | null>(null);
  const viewerRef = useCallback((h: ViewerHandle | null) => {
    handleRef.current = h;
    setHandleState(h);
  }, []);

  /* ── tool preferences & input policy ── */
  const [prefs, setPrefs] = useState<ToolPrefs>(loadToolPrefs);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const updatePrefs = useCallback((fn: (p: ToolPrefs) => ToolPrefs) => {
    const next = fn(prefsRef.current);
    prefsRef.current = next;
    saveToolPrefs(next);
    setPrefs(next);
  }, []);
  const [fingerPref, setFingerPref] = useState<FingerDrawPref>(readFingerDraw);
  const [penSeen, setPenSeen] = useState(readPenSeen);
  const penSeenRef = useRef(penSeen);
  useEffect(() => {
    const sync = () => setFingerPref(readFingerDraw());
    window.addEventListener(FINGER_DRAW_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(FINGER_DRAW_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  /* ── panels ── */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const toolBeforePalette = useRef<ToolId | null>(null);
  const [layersOpen, setLayersOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [recentGlyphs, setRecentGlyphs] = useState<string[]>(loadRecentGlyphs);
  const [currentPage, setCurrentPage] = useState(0);
  const [staleDismissed, setStaleDismissed] = useState(false);

  /**
   * Adds to the active own layer (the engine creates "Mitt lag" and un-hides the layer as needed).
   * The engine drops an add silently (layer full: MAX_LIVE_OBJECTS_PER_LAYER, data over 32 KB); its
   * index is updated synchronously by apply(), so a missing object means it was refused → toast, null.
   */
  const addObject = useCallback<AddObject>(
    <K extends AnnotationKind>(page: number, kind: K, data: DataByKind[K], target?: SheetAnnotations): AnnotationOf<K> | null => {
      const eng = target ?? engineRef.current;
      const obj = eng.newObject(page, kind, data);
      eng.apply([{ type: 'add', obj }]);
      if (eng.get(obj.id)) return obj;
      toast.show(t('annotate.add_rejected'), { tone: 'error' });
      return null;
    },
    [toast, t],
  );

  const clearSelection = useCallback(() => setSelectedId(null), []);
  /** A new text is selected right away (move / resize handles + actions), unless the sheet changed. */
  const onTextCreated = useCallback((obj: AnnotationOf<'text'>, eng: SheetAnnotations) => {
    if (eng === engineRef.current) setSelectedId(obj.id);
  }, []);
  const textBox = useTextBox({ engineRef, handleRef, addObject, onDeleted: clearSelection, onCreated: onTextCreated });
  const conn = useConnectorFlow({ engine, engineRef, handleRef, addObject, color: prefs.connector.color, size: prefs.connector.size });
  const { flow } = conn;

  const tool = prefs.tool;
  const drawFingers = fingersDraw(fingerPref, penSeen);
  const choosingTarget = flow?.step === 'target';
  const role = fingerRole(tool, drawFingers, choosingTarget);
  const grab = !choosingTarget && fingerGrabsSelection(tool, drawFingers);

  /* ── engine → overlay store ── */
  useLayoutEffect(() => {
    store.setObjects(engine.objectsByPage);
  }, [store, engine.objectsByPage]);

  const selectedRaw = selectedId ? engine.get(selectedId) : undefined;
  const selected =
    selectedRaw &&
    selectedRaw.deletedAt === null &&
    engine.isMine(selectedRaw.id) &&
    engine.layers.some((l) => l.id === selectedRaw.layerId && l.visible)
      ? selectedRaw
      : null;
  // Drop a selection whose object went away (undo, delete, hidden layer, remote change) — but not
  // one that is simply not in the engine yet (a just-created duplicate).
  const seenSelected = useRef<string | null>(null);
  useEffect(() => {
    if (selected) seenSelected.current = selected.id;
    else if (selectedId && seenSelected.current === selectedId) {
      seenSelected.current = null;
      setSelectedId(null);
    }
  }, [selected, selectedId]);

  const selConn = selected?.kind === 'connector' ? selected : null;
  const targetMark = useMemo(
    () => (selConn ? { page: selConn.data.target.page, x: unquant(selConn.data.target.at[0]), y: unquant(selConn.data.target.at[1]) } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selConn?.id, selConn?.hlcMs],
  );
  const draftMark = useMemo(
    () =>
      flow && !flow.editId
        ? { page: flow.source.page, x: flow.source.x, y: flow.source.y, color: prefs.connector.color, size: prefs.connector.size }
        : null,
    [flow, prefs.connector.color, prefs.connector.size],
  );
  const hiddenId = textBox.text?.id ?? null;
  const scalable = !!selected && selectionScalable(selected.kind, tool);
  useLayoutEffect(() => {
    store.setUi({
      editing,
      selectedId: editing && selected ? selected.id : null,
      target: editing ? targetMark : null,
      draft: editing ? draftMark : null,
      hiddenId,
      grab,
      scalable,
    });
  }, [store, editing, selected, targetMark, draftMark, hiddenId, grab, scalable]);

  /** Own object top-most at a page point (`tolPx` CSS px tolerance at the current zoom). */
  const pick = useCallback(
    (pt: PagePoint, accept?: (o: AnnotationObject) => boolean, tolPx = HIT_TOLERANCE_PX): AnnotationObject | null => {
      const rect = pageClientRect(handleRef.current, pt.page);
      const w = rect?.width ?? 800;
      const H = pageHeightUnits(pages[pt.page]);
      const eng = engineRef.current;
      return topmostHit(
        store.getPage(pt.page),
        normToUnits(pt, H),
        cssToUnits(tolPx, w),
        hitContext(H, w),
        (o) => eng.isMine(o.id) && (!accept || accept(o)),
      );
    },
    [pages, store],
  );

  /** Own object a tap with the current tool selects (routing.ts: kinds and tolerance per tool). */
  const toolPick = useCallback(
    (pt: PagePoint): AnnotationObject | null => {
      const tl = prefsRef.current.tool;
      return pick(pt, (o) => tapSelects(tl, o.kind), tapTolerancePx(tl));
    },
    [pick],
  );

  /**
   * Is the page point on the selection `o`: inside its (dashed) frame — or, for ink / a highlight in
   * Penni / Yfirstrikun, on the mark itself (routing.ts onSelectionArea)?
   */
  const onSelection = useCallback(
    (pt: PagePoint, o: AnnotationObject): boolean => {
      if (o.page !== pt.page) return false;
      const rect = pageClientRect(handleRef.current, pt.page);
      const w = rect?.width ?? 800;
      const H = pageHeightUnits(pages[pt.page]);
      return onSelectionArea(o, normToUnits(pt, H), prefsRef.current.tool, hitContext(H, w), w);
    },
    [pages],
  );

  /* ── taps (from the input hook) ── */

  /** Resolves and performs a tap (routing.ts resolveTap); 'draw' leaves a stroke tool's dot to the caller. */
  const onTap = useCallback(
    (pt: PagePoint, client: { x: number; y: number }, _pointerType: string, from: TapFrom): TapAction => {
      if (conn.flowRef.current?.step === 'target') {
        conn.chooseTarget(pt);
        return 'keep';
      }
      const p = prefsRef.current;
      const eng = engineRef.current;
      const sel = from.selectedId ? eng.get(from.selectedId) : undefined;
      let hit = toolPick(pt);
      if (!hit && sel && sel.deletedAt === null && eng.isMine(sel.id) && onSelection(pt, sel)) hit = sel;
      const action = resolveTap({
        tool: p.tool,
        hit,
        selectedId: from.selectedId,
        onHandle: from.onHandle,
        hasGlyph: !!p.stamp.glyph,
        selects: from.selects,
      });
      switch (action) {
        case 'select':
        case 'keep':
          if (hit) setSelectedId(hit.id);
          break;
        case 'edit-text':
          if (hit?.kind === 'text') textBox.edit(hit);
          break;
        case 'create-text':
          setSelectedId(null);
          textBox.openNew(pt.page, [quant(pt.x), quant(pt.y)], p.text.size, p.text.color, client);
          break;
        case 'place-stamp': {
          setSelectedId(null);
          const glyph = p.stamp.glyph;
          if (!glyph) break;
          const obj = addObject(pt.page, 'stamp', { at: [quant(pt.x), quant(pt.y)], glyph, color: p.stamp.color, size: p.stamp.size });
          if (!obj) break;
          // The new symbol is selected at once: drag it into place or resize it; the next tap on
          // empty space places another one.
          setSelectedId(obj.id);
          setRecentGlyphs((list) => {
            const next = pushRecent(list, glyph);
            saveRecentGlyphs(next);
            return next;
          });
          break;
        }
        case 'choose-symbol':
          setSelectedId(null);
          setPaletteOpen(true);
          break;
        case 'start-connector':
          setSelectedId(null);
          conn.start(pt);
          break;
        case 'deselect':
          setSelectedId(null);
          break;
        case 'draw':
          break;
      }
      return action;
    },
    [conn, toolPick, onSelection, textBox, addObject],
  );

  /* ── commits (from the input hook) ── */

  const commitInk = useCallback(
    (page: number, pts: RawPt[]) => {
      const q = quantiseStroke(pts);
      if (q.length < 3) {
        store.clearWet(page);
        return;
      }
      const p = prefsRef.current.pen;
      const obj = addObject(page, 'ink', { pts: q, color: p.color, width: PEN_WIDTHS[p.width] ?? PEN_WIDTHS[1] });
      if (obj) store.holdWet(page, obj.id);
      else store.clearWet(page);
    },
    [addObject, store],
  );

  const commitHighlight = useCallback(
    (page: number, a: Pt, b: Pt) => {
      const h = prefsRef.current.highlighter;
      const obj = addObject(page, 'highlight', {
        a: [quant(a.x), quant(a.y)],
        b: [quant(b.x), quant(b.y)],
        color: h.color,
        width: HIGHLIGHT_WIDTHS[h.width] ?? HIGHLIGHT_WIDTHS[1],
      });
      if (obj) store.holdWet(page, obj.id);
      else store.clearWet(page);
    },
    [addObject, store],
  );

  /** All objects touched by one eraser drag go in ONE apply (= one undo step). */
  const commitErase = useCallback(
    (ids: string[], page: number) => {
      const eng = engineRef.current;
      const hlc = new Map(ids.map((id) => [id, eng.get(id)?.hlcMs ?? 0] as [string, number]));
      eng.apply(ids.map((id) => ({ type: 'delete' as const, id })));
      store.settle('erase', page, hlc);
    },
    [store],
  );

  const commitMove = useCallback(
    (id: string, dx: number, dy: number): boolean => {
      const eng = engineRef.current;
      const o = eng.get(id);
      if (!o || o.deletedAt !== null || !eng.isMine(id)) return false;
      const d = clampDelta(o, Math.round(dx * Q), Math.round(dy * Q));
      if (d.dx === 0 && d.dy === 0) return false;
      eng.apply([{ type: 'update', id, data: translateData(o, d.dx, d.dy) }]);
      store.settle('preview', o.page, new Map([[id, o.hlcMs]]));
      return true;
    },
    [store],
  );

  const commitScale = useCallback(
    (id: string, origin: Pt, k: number, boxW: number): boolean => {
      const eng = engineRef.current;
      const o = eng.get(id);
      if (!o || o.deletedAt !== null || !eng.isMine(id)) return false;
      // A text, symbol or pin already at its size limit (a pin: or still at the visible minimum): nothing changes, no undo step.
      if (isSized(o) && effectiveScale(o, k, boxW) === 1) return false;
      eng.apply([{ type: 'update', id, data: scaleData(o, [quant(origin.x), quant(origin.y)], k, boxW) }]);
      store.settle('preview', o.page, new Map([[id, o.hlcMs]]));
      return true;
    },
    [store],
  );

  /** First Apple Pencil contact: remember it; in 'auto' mode fingers stop drawing (one-time toast). */
  const notePen = useCallback(() => {
    if (penSeenRef.current) return;
    penSeenRef.current = true;
    writePenSeen();
    setPenSeen(true);
    if (readFingerDraw() === 'auto') toast.show(t('annotate.pen_detected'), { ms: 7000 });
  }, [toast, t]);

  /* ── edit-mode input ── */

  const live = useRef<EditInputLive | null>(null);
  useLayoutEffect(() => {
    live.current = {
      tool,
      role,
      fingersDraw: drawFingers,
      choosingTarget,
      textOpen: textBox.text !== null,
      selectedId: selected?.id ?? null,
      store,
      pages,
      inkStyle: { color: prefs.pen.color, width: PEN_WIDTHS[prefs.pen.width] ?? PEN_WIDTHS[1] },
      highlightStyle: { color: prefs.highlighter.color, width: HIGHLIGHT_WIDTHS[prefs.highlighter.width] ?? HIGHLIGHT_WIDTHS[1] },
      isMine: (id) => engineRef.current.isMine(id),
      getObject: (id) => engineRef.current.get(id),
      pick: toolPick,
      // A long press selects any own mark but a connector, on the mark itself (routing.ts).
      pressPick: (pt) => pick(pt, (o) => longPressSelects(o.kind), LONG_PRESS_TOLERANCE_PX),
      inSelection: (pt) => {
        const o = selected ? engineRef.current.get(selected.id) : undefined;
        return !!o && o.deletedAt === null && onSelection(pt, o);
      },
      select: setSelectedId,
      notePen,
      commitText: textBox.commit,
      tap: onTap,
      commitInk,
      commitHighlight,
      commitErase,
      commitMove,
      commitScale,
    };
  });
  // The engine accepts edits once its local database has been read.
  const ready = engine.status === 'ready';
  useEditInput(editing && ready, handle, live);

  /* ── selection actions ── */

  const deleteSelected = useCallback(() => {
    const id = selected?.id;
    if (!id) return;
    engineRef.current.apply([{ type: 'delete', id }]);
    setSelectedId(null);
  }, [selected?.id]);

  const recolorSelected = useCallback(
    (color: string) => {
      if (!selected || selected.data.color === color) return;
      engineRef.current.apply([{ type: 'update', id: selected.id, data: { ...selected.data, color } }]);
    },
    [selected],
  );

  /** "Afrita": a copy with a new id, slightly offset, which becomes the selection. */
  const duplicateSelected = useCallback(() => {
    if (!selected) return;
    let d = clampDelta(selected, DUPLICATE_OFFSET, DUPLICATE_OFFSET);
    if (d.dx === 0 && d.dy === 0) d = clampDelta(selected, -DUPLICATE_OFFSET, -DUPLICATE_OFFSET);
    const copy = addObject(selected.page, selected.kind, translateData(selected, d.dx, d.dy));
    if (!copy) return;
    seenSelected.current = null;
    setSelectedId(copy.id);
  }, [selected, addObject]);

  /** Size of the selected text, symbol or pin one "A−" / "A+" step away (a pin: from its size as drawn on its page). */
  const nextSelectedSize = useCallback(
    (dir: 1 | -1): number | null => {
      if (!selected || !isSized(selected)) return null;
      const boxW = selected.kind === 'connector' ? pageClientRect(handleRef.current, selected.page)?.width : undefined;
      return stepSize(selected.kind, sizeOf(selected), dir, boxW);
    },
    [selected],
  );

  /** "A−" / "A+": one size step of the selected text, symbol or connector pin (one undo step per press). */
  const resizeSelected = useCallback(
    (dir: 1 | -1) => {
      if (!selected || !isSized(selected)) return;
      const size = nextSelectedSize(dir);
      if (size === null || size === sizeOf(selected)) return;
      engineRef.current.apply([{ type: 'update', id: selected.id, data: { ...selected.data, size } }]);
    },
    [selected, nextSelectedSize],
  );

  const editSelectedText = useCallback(() => {
    if (selected?.kind === 'text') textBox.edit(selected);
  }, [selected, textBox]);

  /** "Prófa": the real jump (pulse + "↩ Til baka"). */
  const testConnector = useCallback(() => {
    if (selected?.kind !== 'connector') return;
    const d = selected.data;
    handleRef.current?.jumpTo(d.target.page, unquant(d.target.at[0]), unquant(d.target.at[1]), {
      source: { page: selected.page, x: unquant(d.at[0]), y: unquant(d.at[1]) },
    });
  }, [selected]);

  const retargetConnector = useCallback(() => {
    if (selected?.kind === 'connector') conn.retarget(selected);
  }, [selected, conn]);

  /* ── tools & panels ── */

  const setTool = useCallback(
    (next: ToolId) => {
      textBox.commit();
      conn.cancel();
      setSelectedId(null);
      if (next === 'stamp') {
        if (prefsRef.current.tool !== 'stamp') toolBeforePalette.current = prefsRef.current.tool;
        setPaletteOpen(true);
      }
      updatePrefs((p) => ({ ...p, tool: next }));
    },
    [textBox, conn, updatePrefs],
  );

  const confirmGlyph = useCallback(
    (glyph: string, color: string, size: number) => {
      updatePrefs((p) => ({ ...p, tool: 'stamp', stamp: { glyph, color, size } }));
      setRecentGlyphs((list) => {
        const next = pushRecent(list, glyph);
        saveRecentGlyphs(next);
        return next;
      });
      setPaletteOpen(false);
    },
    [updatePrefs],
  );

  const closePalette = useCallback(() => {
    setPaletteOpen(false);
    // Cancelled before any symbol was ever chosen: go back to the previous tool.
    if (!prefsRef.current.stamp.glyph && prefsRef.current.tool === 'stamp') {
      updatePrefs((p) => ({ ...p, tool: toolBeforePalette.current ?? 'pen' }));
    }
  }, [updatePrefs]);

  const closeCoach = useCallback(() => {
    writeCoachDone();
    setCoachOpen(false);
  }, []);

  /** Pins are tappable in edit mode with Tengill / Velja: select an own connector. */
  const onPinTap = useCallback(
    (pin: ConnectorPin) => {
      if (engineRef.current.isMine(pin.id)) setSelectedId(pin.id);
      else toast.show(t('annotate.not_yours'));
    },
    [toast, t],
  );

  const undo = useCallback(() => {
    textBox.commit();
    engineRef.current.undo();
  }, [textBox]);
  const redo = useCallback(() => {
    textBox.commit();
    engineRef.current.redo();
  }, [textBox]);

  /* ── entering / leaving edit mode ── */

  const wasEditing = useRef(editing);
  useEffect(() => {
    if (wasEditing.current === editing) return;
    wasEditing.current = editing;
    if (editing) {
      if (!readCoachDone()) setCoachOpen(true);
      return;
    }
    textBox.commit();
    conn.cancel();
    setSelectedId(null);
    setPaletteOpen(false);
    setLayersOpen(false);
    setCoachOpen(false);
    store.setUi({ preview: null });
    store.clearErasing();
    engineRef.current.sync.flush().catch(() => undefined);
  }, [editing, textBox, conn, store]);

  // Leaving the song page — or switching sheets with the file chips — with the text box open keeps
  // what was typed: layout cleanup runs before the engine hook releases the old sheet's store, and
  // the draft carries the engine it was opened on.
  const commitTextRef = useRef(textBox.commit);
  commitTextRef.current = textBox.commit;
  useLayoutEffect(() => () => commitTextRef.current(), [file.id]);

  // A different sheet (file chips): drop everything tied to the previous one.
  const { reset: resetFlow } = conn;
  const { reset: resetText } = textBox;
  useEffect(() => {
    setSelectedId(null);
    resetFlow();
    resetText();
    setStaleDismissed(false);
  }, [file.id, resetFlow, resetText]);

  /* ── keyboard (edit mode) ── */
  const keyRef = useRef({ undo, redo, deleteSelected, cancelFlow: conn.cancel, clearSelection, hasSelection: !!selected, hasFlow: !!flow });
  keyRef.current = { undo, redo, deleteSelected, cancelFlow: conn.cancel, clearSelection, hasSelection: !!selected, hasFlow: !!flow };
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target) || e.defaultPrevented) return;
      // A sheet/dialog is open (Lög, Deila, Tákn, coach marks): its keys are not for the page.
      if (document.querySelector('[aria-modal="true"]')) return;
      const k = keyRef.current;
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) k.redo();
        else k.undo();
      } else if (mod && key === 'y') {
        e.preventDefault();
        k.redo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && k.hasSelection) {
        e.preventDefault();
        k.deleteSelected();
      } else if (e.key === 'Escape') {
        if (k.hasFlow) k.cancelFlow();
        else if (k.hasSelection) k.clearSelection();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  const pins = useStablePins(engine.connectors);
  const pinsInteractive = editing && !choosingTarget && (tool === 'select' || tool === 'connector');
  const ctx = useMemo(() => ({ store, pages }), [store, pages]);
  const goToPage = useCallback((p: number) => handleRef.current?.goToPage(p), []);
  /** The toolbar's page buttons: turn while any tool stays active (no need to leave Skrifa). */
  const turnPage = useCallback((dir: 1 | -1) => handleRef.current?.turnPage?.(dir), []);
  const dismissStale = useCallback(() => setStaleDismissed(true), []);

  return {
    engine,
    ready,
    ctx,
    pages,
    file,
    viewerRef,
    prefs,
    updatePrefs,
    tool,
    setTool,
    // Only while the input hook is attached (it pans in JS); before that the page scrolls natively.
    editTouchAction: (ready && role === 'draw' ? 'none' : 'pan-x pan-y') as 'none' | 'pan-x pan-y',
    pins,
    pinsInteractive,
    onPinTap,
    undo,
    redo,
    selected,
    clearSelection,
    deleteSelected,
    recolorSelected,
    duplicateSelected,
    nextSelectedSize,
    resizeSelected,
    editSelectedText,
    testConnector,
    retargetConnector,
    flow,
    cancelFlow: conn.cancel,
    saveConnector: conn.save,
    labelSuggestions: conn.labelSuggestions,
    text: textBox.text,
    textAreaRef: textBox.textAreaRef,
    setTextValue: textBox.setValue,
    commitText: textBox.commit,
    cancelText: textBox.cancel,
    paletteOpen,
    setPaletteOpen,
    closePalette,
    confirmGlyph,
    recentGlyphs,
    layersOpen,
    setLayersOpen,
    coachOpen,
    closeCoach,
    currentPage,
    setCurrentPage,
    goToPage,
    turnPage,
    staleVisible: engine.hasStaleLayers && !staleDismissed,
    dismissStale,
  };
}

export type Annotate = ReturnType<typeof useAnnotate>;
